import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
  FormGroup, FormRow,
  Label, Select,
} from './styles';
import { IAppState } from '../../../state/reducers';
import {
  valueToOption, valuesToOptions, lookupToOption,
  scheduleToOption, schedulesToOptions, GameOption,
} from './utils';
import { IExchangeSchedule } from '../../../api/vault/exchangeSchedule';
import { IVaultTeam } from '../../../api/vault/teams';
import {
  FormType, IconMap, updateFilters,
  updateSelectedSchedule,
} from '../../../state/exchange/gameScheduleForm';
import {
  ILookupOption,
  selectExchangeSportOptions,
  selectLeagueOptions,
} from '../../../state/lookups';
import { IScheduleOption } from './interfaces';

const FilterTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  padding: 12px;
`;

const FilterContainer = styled.div`
  margin-bottom: 16px;
  border: 1px solid black;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
`;

const defaultGameType = 'Regular Season';

class Filters extends PureComponent<IStateProps & IDispatchProps> {
  isCreateMode = this.props.formType === FormType.Create;
  isUpdateMode = this.props.formType === FormType.Update;

  onUpdateSeason = (season: string) => {
    const {
      sportFilter,
      leagueFilter,
      updateFilters,
    } = this.props;
    updateFilters(sportFilter, leagueFilter, season);
  };

  onUpdateSport = (sport: string) => {
    const {
      leagueFilter,
      seasonFilter,
      updateFilters,
    } = this.props;
    updateFilters(sport, leagueFilter, seasonFilter);
  };

  onUpdateLeague = (league: string) => {
    const {
      sportFilter,
      seasonFilter,
      updateFilters,
    } = this.props;
    updateFilters(sportFilter, league, seasonFilter);
  };

  renderSeasonAndGame = () => {
    const {
      seasonFilter, seasons,
      selectedSchedule, exchangeSchedules,
      loggedInUsersTeam,
      sportFilter,
      leagueFilter,
      teamIcons,
      sourceTeam,
      updateSelectedSchedule,
    } = this.props;

    let schedules = exchangeSchedules
      .filter(({ Season }) => Season === seasonFilter)
      .sort((a, b) => {
        const aTime = new Date(a.GameDate).getTime();
        const bTime = new Date(b.GameDate).getTime();
        if (aTime > bTime) return 1;
        if (aTime < bTime) return -1;
        return 0;
      });

    if (sportFilter) schedules = schedules.filter(({ Sport }) => Sport === sportFilter);
    if (leagueFilter) schedules = schedules.filter(({ League }) => League === leagueFilter);

    const teamData = { teamIcons, source: sourceTeam };
    const shooterTeam = sourceTeam || loggedInUsersTeam;

    return (
      <FormRow>
        <FormGroup>
          <Label data-auto="SeasonSelectLabel">Season *</Label>
          <Select
            value={seasonFilter ? valueToOption(seasonFilter) : null}
            options={valuesToOptions(seasons)}
            onChange={(option: any) => this.onUpdateSeason(option.value)}
          />
        </FormGroup>
        <FormGroup
          disabled={!sportFilter || !seasonFilter}
        >
          <Label data-auto="GameSelectLabel">Game</Label>
          <Select
            value={selectedSchedule ? scheduleToOption(selectedSchedule, teamData) : null}
            options={schedulesToOptions(schedules, teamData, false, shooterTeam, defaultGameType, seasonFilter)}
            onChange={(option: unknown) => updateSelectedSchedule((option as IScheduleOption)?.value)}
            isDisabled={!sportFilter || !seasonFilter}
            components={{
              Option: GameOption,
            }}
            data-auto="GameSelect"
          />
        </FormGroup>
      </FormRow>
    );
  };

  renderSportAndLeague = () => {
    const {
      sportFilter,
      leagueOptions,
      leagueFilter,
      exchangeSportOptions,
    } = this.props;

    if (!(this.isCreateMode || this.isUpdateMode)) return null;

    return (
      <FormRow>
        <FormGroup>
          <Label data-auto="SportSelectLabel">Sport *</Label>
          <Select
            value={lookupToOption(sportFilter, exchangeSportOptions)}
            options={exchangeSportOptions}
            onChange={(option: any) => this.onUpdateSport(option.value)}
          />
        </FormGroup>
        <FormGroup>
          <Label data-auto="LeagueSelectLabel">League</Label>
          <Select
            value={lookupToOption(leagueFilter, leagueOptions)}
            options={leagueOptions}
            onChange={(option: any) => this.onUpdateLeague(option.value)}
          />
        </FormGroup>
      </FormRow>
    );
  };

  render() {
    return (
      <FilterContainer>
        <FilterTitle>Filters</FilterTitle>
        {this.renderSeasonAndGame()}
        {this.renderSportAndLeague()}
      </FilterContainer>
    );
  }
}

interface IStateProps {
  formType: FormType;
  seasons: string[];
  exchangeSchedules: IExchangeSchedule[];
  selectedSchedule: IExchangeSchedule;
  seasonFilter: string;
  sportFilter: string;
  leagueFilter: string;
  leagueOptions: ILookupOption[];
  exchangeSportOptions: ILookupOption[];
  teamIcons: IconMap;
  sourceTeam: IVaultTeam;
  loggedInUsersTeam: IVaultTeam;
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  formType: state.exchange.gameScheduleForm.formType,
  seasons: state.exchange.gameScheduleForm.seasons,
  exchangeSchedules: state.exchange.gameScheduleForm.exchangeSchedules,
  selectedSchedule: state.exchange.gameScheduleForm.selectedSchedule,
  seasonFilter: state.exchange.gameScheduleForm.seasonFilter,
  sportFilter: state.exchange.gameScheduleForm.sportFilter,
  leagueFilter: state.exchange.gameScheduleForm.leagueFilter,
  leagueOptions: selectLeagueOptions(state),
  exchangeSportOptions: selectExchangeSportOptions(state),
  teamIcons: state.exchange.gameScheduleForm.teamIcons,
  sourceTeam: state.exchange.gameScheduleForm.sourceTeam,
  loggedInUsersTeam: state.exchange.initExchange.currentTeam,
});

interface IDispatchProps {
  updateFilters: (sportFilter: string, leagueFilter: string, seasonFilter: string) => void;
  updateSelectedSchedule: (schedule: IExchangeSchedule) => void;
}

const mapDispatchToProps: IDispatchProps = {
  updateFilters,
  updateSelectedSchedule,
};

export default connect(mapStateToProps, mapDispatchToProps)(Filters);
